// src/components/FleetMobile.js
import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { FaUser } from "react-icons/fa";
import cadillacXTS from "../assets/images/Cadillac XTS.png";
import lincolnMKT from "../assets/images/Lincoln MKT.png";
import cadillacEscalade from "../assets/images/Cadillac Escalade.png";
import mercedesSprinter from "../assets/images/Mercedes Sprinter.png";

const fleetVehicles = [
  {
    id: 1,
    title: "Cadillac XTS",
    passengers: 4,
    description:
      "For up to 4 passengers, Heritage Car Airports delivers an elegant and prompt taxi service—perfect for local trips or airport transfers. Enjoy a luxurious ride at the speed you need.",
    image: cadillacXTS,
  },
  {
    id: 2,
    title: "Lincoln MKT",
    passengers: 5,
    description:
      "Travel in style with the Lincoln MKT, accommodating up to 5 passengers. Heritage Car Airports offers a swift and comfortable taxi service, ideal for both business and leisure journeys.",
    image: lincolnMKT,
  },
  {
    id: 3,
    title: "Cadillac Escalade",
    passengers: 7,
    description:
      "Spacious and luxurious, our Cadillac Escalade is designed for groups of up to 7. Heritage Car Airports ensures a premium taxi ride with reliability and punctuality for your local and airport transfers.",
    image: cadillacEscalade,
  },
  {
    id: 4,
    title: "Mercedes Sprinter",
    passengers: 15,
    description:
      "Ideal for large groups, the Mercedes Sprinter comfortably transports up to 15 passengers. Heritage Car Airports offers a secure, timely taxi service—perfect for special events and group travel.",
    image: mercedesSprinter,
  },
];

// Estilos en objetos
const styles = {
  container: {
    padding: "20px",
    background: "#f5f5f5",
  },
  descriptionBar: {
    textAlign: "center",
    marginBottom: "20px",
    padding: "10px 20px",
  },
  descriptionText: {
    fontSize: "0.9rem",
    lineHeight: 1.4,
    maxWidth: "800px",
    margin: "auto",
    color: "#2B3F50",
  },
  fleetGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "15px",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: "20px",
    boxShadow: "0 6px 12px rgba(0, 0, 0, 0.1)",
    display: "flex",
    flexDirection: "column",
    position: "relative", // Para overlay
  },
  topStripe: {
    height: "50px",
    background: "linear-gradient(to right, #0f2027, #203a43, #2c5364)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 10px",
    borderTopLeftRadius: "20px",
    borderTopRightRadius: "20px",
  },
  cardTitle: {
    fontSize: "0.9rem",
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  passengerInfo: {
    display: "flex",
    alignItems: "center",
    fontWeight: "bold",
    fontSize: "0.9rem",
  },
  imageContainer: {
    position: "relative",
    overflow: "hidden",
  },
  cardImage: {
    width: "100%",
    height: "180px",
    objectFit: "cover",
  },
  hoverOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "linear-gradient(rgba(0,80,160,0.4), rgba(0,80,160,0.7))",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: "0.9rem",
    fontWeight: "bold",
    textAlign: "center",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  cardBody: {
    padding: "15px",
    flex: 1,
  },
  description: {
    fontSize: "0.8rem",
    color: "#555",
    lineHeight: "1.3",
  },
  quoteButton: {
    backgroundColor: "#0066cc",
    color: "#fff",
    padding: "10px 14px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "0.9rem",
    margin: "15px",
    border: "none",
  },
};

export default function FleetMobile() {
  // useState para manejar el "hover" o "tap" en móvil
  const [hoveredVehicle, setHoveredVehicle] = useState(null);

  // JSON-LD LocalBusiness (ajusta datos reales)
  const localBusinessJsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Heritage Car Airport",
    "image": "https://heritagecarairport.com/images/logo.jpg",
    "@id": "https://heritagecarairport.com/",
    "url": "https://heritagecarairport.com/",
    "telephone": "+1-914-276-2961",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "395 Hallocks Mill Rd",
      "addressLocality": "Yorktown Heights",
      "addressRegion": "NY",
      "postalCode": "10598",
      "addressCountry": "US"
    },
    "priceRange": "$$",
    "description": "Premium taxi service for local trips and airport transfers, available 24/7."
  };

  return (
    <>
      {/* SEO Avanzado con react-helmet */}
      <Helmet>
        <title>Heritage Car Airports - Our Fleet</title>
        <meta
          name="description"
          content="Discover our premium fleet designed for efficient taxi rides. Choose the ideal vehicle for your group size."
        />
        <meta name="robots" content="index,follow" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Heritage Car Airports - Fleet" />
        <meta
          property="og:description"
          content="View our premium vehicles for local trips and airport transfers."
        />
        <meta property="og:url" content="https://heritagecarairport.com/fleet" />
        <meta
          property="og:image"
          content="https://heritagecarairport.com/images/your-og-image.jpg"
        />

        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Heritage Car Airports - Fleet" />
        <meta
          name="twitter:description"
          content="Check out our luxurious fleet, perfect for local trips or airport transfers."
        />
        <meta
          name="twitter:image"
          content="https://heritagecarairport.com/images/your-og-image.jpg"
        />

        {/* JSON-LD LocalBusiness */}
        <script type="application/ld+json">
          {JSON.stringify(localBusinessJsonLd)}
        </script>
      </Helmet>

      <div style={styles.container}>
        <div style={styles.descriptionBar}>
          <h2 className="descriptionTitle">Fleet at Heritage Car Airports</h2>
          <p style={styles.descriptionText}>
            Discover our premium fleet designed for efficient taxi rides. Choose
            the ideal vehicle for your group size and experience prompt,
            reliable service for local trips and airport transfers.
          </p>
        </div>

        <div style={styles.fleetGrid}>
          {fleetVehicles.map((vehicle) => (
            <div
              key={vehicle.id}
              className="fleet-card"
              style={styles.card}
              itemScope
              itemType="http://schema.org/Product"
            >
              <div style={styles.topStripe}>
                <span style={styles.cardTitle} itemProp="name">
                  {vehicle.title}
                </span>
                <div style={styles.passengerInfo}>
                  <FaUser style={{ marginRight: 5 }} />
                  <span>{vehicle.passengers}</span>
                </div>
              </div>

              {/* Contenedor imagen con overlay */}
              <div
                style={styles.imageContainer}
                onMouseEnter={() => setHoveredVehicle(vehicle.id)}
                onMouseLeave={() => setHoveredVehicle(null)}
                onTouchStart={() => setHoveredVehicle(vehicle.id)}
                onTouchEnd={() => setHoveredVehicle(null)}
              >
                <img
                  src={vehicle.image}
                  alt={vehicle.title}
                  style={styles.cardImage}
                  itemProp="image"
                />
                {/* Overlay si hoveredVehicle === vehicle.id */}
                {hoveredVehicle === vehicle.id && (
                  <div style={{ ...styles.hoverOverlay, opacity: 1 }}>
                    <div>{vehicle.title}</div>
                    <div>9142762961</div>
                  </div>
                )}
              </div>

              <div style={styles.cardBody} itemProp="description">
                <p style={styles.description}>{vehicle.description}</p>
              </div>

              <button
                type="button"
                className="quote-button"
                style={styles.quoteButton}
              >
                BOOK NOW
              </button>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
